import React, { FC, useContext, useRef, useState, useEffect, MutableRefObject } from 'react';
import { useParams } from 'react-router-dom';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import useDebounce from '@empiriecom/module-components/hooks/useDebounce';
import { FragmentConfiguration } from '@/config';
import { Rating } from '@/interfaces/Rating';
import { SeoDataInterface, Product } from '@/interfaces';
import { useData } from 'react-isomorphic-data';
import { DataState } from 'react-isomorphic-data/dist/types/hooks/types';
import { useIntl } from 'react-intl';
import { BreadcrumbData } from '@empiriecom/module-components/Breadcrumb/types';
import useHashParameters from '../hooks/useHashParameters';
import useUrlParameters from '../hooks/useUrlParameters';
import ProductContext, { DataContextType } from './DataContext';
import { isClientSide } from '../utils';
import { InputError } from '../ProductSelection/Input/types';

const regex = new RegExp(/(AKL(BB)?\d+)|([0-9]{6,8}[a-z0-9]{0,2}(-[a-z0-9,]+)?(-[0-9]+)?)/, 'i');
export const verifyProductId = (productId: string): string | null =>
  productId && regex.test(productId) ? productId : null;

export const useProductContext = (): DataContextType => useContext(ProductContext);

/** This component provides the data context for the Product Detail View pages
 * @param {string} productId The productId
 * @param {boolean} isBot Toggle bot recognition. default false
 */
export const DataProvider: FC<{ productId?: string; isBot?: boolean }> = ({
  children,
  productId: serverProductId,
  isBot = false,
  ...props
}) => {
  let akl: string;
  let fallbackId: string;
  /* state data */
  const {
    api: apiConfig,
    company,
    customerRatings: configRating,
    meta: metaConfig,
    seo: seoConfig,
  } = useConfig<FragmentConfiguration>();
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [quantity, setQuantity] = useState<number>(1);
  const [measureLength, setMeasureLength] = useState<number>(null);
  const [measureWidth, setMeasureWidth] = useState<number>(null);
  const [isEngravingError, setIsEngravingError] = useState<null | string>(null);
  const [isShowEngravingError, setIsShowEnravingError] = useState<boolean>(false);
  const [engravingValue, setEngravingValue] = useState<string>(null);
  const [sku, setSKU] = useState<string>('');
  const [atbRequested, setAtbRequested] = useState<boolean>(false);
  const [lengthInput, setLengthInput] = useState<MutableRefObject<HTMLInputElement>>(null);
  const [widthInput, setWidthInput] = useState<MutableRefObject<HTMLInputElement>>(null);
  const [lengthErr, setLengthErr] = useState<InputError>(null);
  const [widthErr, setWidthErr] = useState<InputError>(null);
  const [isFallback, setIsFallback] = useState<Boolean>(false);

  const {
    category,
    variationId,
    query,
    printProductId,
    length,
    width,
    engraving,
  } = useHashParameters();
  const { productId } = useParams<{ productId: string }>();
  const { locale } = useIntl();
  const productDataRef = useRef(null);
  const variantRef = useRef(null);
  const breadcrumbRef = useRef<DataState<BreadcrumbData>>(null);
  const categoryRef = useRef(category);
  const ratingRef = useRef(null);

  useEffect(() => {
    setEngravingValue(engraving || null);
    setMeasureLength(((length as unknown) as number) || null);
    setMeasureWidth(((width as unknown) as number) || null);
  }, [engraving, length, width]);

  /** If no id is present and we are in the browser try to match any AKLBB in the url
   * This may happen for google cache if the page is called with the actual URL as query parameter
   * e.g. https://webcache.googleusercontent.com/search?q=cache:1vkwvr--q3AJ:https://www.imwalking.de/p/pantolette/AKLBB508729872+&cd=1&hl=de&ct=clnk&gl=de
   * */
  if (!productId && !variationId) {
    const href = isClientSide && typeof window.location !== 'undefined' ? window.location.href : '';
    const fallbackMatch = href.match(/(AKLBB[0-9]*)/);
    fallbackId = fallbackMatch ? fallbackMatch[0] : null;
  }
  // eslint-disable-next-line no-underscore-dangle
  const ssrProductId = serverProductId || (isClientSide && (window as any).__productID);

  // handle fallback on clientside
  useEffect(() => {
    if (ssrProductId !== undefined && ssrProductId === productId) {
      setIsFallback(true);
    }
  }, []);
  // handle fallback on serverside
  let isSSRFallback: boolean = false;
  if (!isClientSide && ssrProductId !== undefined && ssrProductId === productId) {
    isSSRFallback = true;
  }

  // determine the correct id to use as variation identifier
  const cleanProductId =
    isFallback || isSSRFallback
      ? ssrProductId
      : variationId ||
        productId ||
        serverProductId ||
        // eslint-disable-next-line no-underscore-dangle
        (isClientSide && (window as any).__productID) ||
        fallbackId;

  const apiProductId = cleanProductId ? cleanProductId?.split('-')[0] : cleanProductId;

  /* Determine host for API calls. react-isomorpic-data needs absolute url for SSR */
  const clientHost = process.env.CLUSTER_ENV === 'staging' ? 'clientHostStaging' : 'clientHost';
  const clusterHost = (process.env.NODE_ENV !== 'development'
    ? apiConfig[isClientSide ? clientHost : 'serverHost']
    : 'https://emma.lan'
  )
    .replace('<namespace>', company.id)
    .replace('<port>', '8888');
  const url = apiProductId
    ? clusterHost.replace('<service>', 'aggreagation-inform-product-store') +
      apiConfig.values.productApiEndpoint
        .replace('<productid>', apiProductId)
        .replace('<locale>', locale)
    : null;

  /* get product data */
  const product = useData<{
    [index: string]: Product;
  }>(
    url,
    {},
    {},
    {
      skip:
        url === null ||
        (productDataRef.current?.data && !!productDataRef.current?.data[cleanProductId]),
    },
  );
  // if we get a response we save it to the ref to prevent empty objects on loading states
  if (typeof product !== 'undefined') {
    productDataRef.current = product;
  }
  // try to find the variant with cleanProductId
  let variant =
    productDataRef.current && productDataRef.current.data
      ? productDataRef.current.data[cleanProductId]
      : undefined;
  // if no variant was found we do a fallback with the data we have
  if (typeof variant === 'undefined' && product && product.data) {
    const [fallbackArtNo, fallbackSize] = cleanProductId ? cleanProductId.split('-') : [];
    const [dataProductId] = Object.keys(product.data);
    // if the fallback starts with the artno we try to select the correct size
    if (dataProductId.startsWith(fallbackArtNo)) {
      variant =
        product && product.data
          ? product.data[`${dataProductId.split('-')[0]}-${fallbackSize}`]
          : undefined;
      // else we just take the first variant in the array
    }
    if (!variant) {
      variant = product && product.data ? product.data[dataProductId] : undefined;
    }
  }
  // if we have data, we get the current akl for subsequent requests
  if (!productDataRef.current.loading && !productDataRef.current.error) {
    akl = typeof variant !== 'undefined' ? variant.akl : null;
  }
  // if we have a variant we save it to the ref object
  if (variant) {
    variantRef.current = variant;
  }

  /* get rating data */
  const ratingParameters = useUrlParameters(configRating.values.summary, { productid: akl }, true);
  const ratingApiUrl =
    variant && variant.akl
      ? clusterHost.replace('<service>', 'router').replace('8888', '9080') + ratingParameters
      : null;
  const rating = useData<Rating>(
    ratingApiUrl,
    {},
    {},
    {
      skip: ratingApiUrl === null,
    },
  );
  if (rating && rating.data) {
    ratingRef.current = rating.data;
  }

  /* get seo data */
  const seoParameters = useUrlParameters(
    seoConfig.enabled ? seoConfig.values.seoApiEndpoint : metaConfig.values.metaApiEndpoint,
    { productid: akl },
  );
  const seoApiUrl =
    variant && variant.akl
      ? clusterHost.replace('<service>', 'aggreagation-inform-seo-store') + seoParameters
      : null;
  const seo = useData<SeoDataInterface>(
    seoApiUrl,
    {},
    {},
    {
      skip: seoApiUrl === null,
    },
  );
  if (!categoryRef.current && seo?.data && seo.data.breadcrumb) {
    categoryRef.current = seo.data.breadcrumb;
  }

  /* get breadcrumb data */
  const breadcrumbParameters = {
    category: categoryRef.current || variantRef.current?.defaultCategory,
    clientId: company.id,
    locale: locale.replace('-', '_'),
  };
  const breadcrumb = useData<BreadcrumbData>(
    `${clusterHost.replace('<service>', 'router').replace('8888', '9080')}${
      apiConfig.values.breadcrumbApiEndpoint
    }`,
    breadcrumbParameters,
    {},
    {
      skip:
        typeof breadcrumbParameters.category === 'undefined' ||
        breadcrumbParameters.category === '',
    },
  );
  if (breadcrumb && breadcrumb.data) {
    breadcrumbRef.current = breadcrumb;
  }

  return (
    <ProductContext.Provider
      value={{
        basketData: {
          addService: (uuid) => {
            let newServices: string[] = [];
            newServices = newServices.concat(selectedServices);
            newServices.push(uuid);
            setSelectedServices(newServices);
          },
          addToBasketRequested: atbRequested,
          quantity,
          removeService: (uuid) => {
            let newServices: string[] = [];
            newServices = newServices.concat(selectedServices);
            newServices = newServices.filter((service) => service !== uuid);
            setSelectedServices(newServices);
          },
          requestedSKU: sku,
          resetServices: () => setSelectedServices([]),
          services: selectedServices,
          setAddToBasketRequested: (requested) => setAtbRequested(requested),
          setQuantity: (quntty) => setQuantity(quntty),
          setRequestedSKU: (requested) => setSKU(requested),
        },
        breadcrumbData: {
          breadcrumb: breadcrumbRef?.current?.data,
          error: breadcrumb?.error,
          isLoading: breadcrumb?.loading,
        },
        categoryData: {
          category: categoryRef.current || variantRef.current?.defaultCategory,
          query,
        },
        madeToMeasureData: {
          engraving: engravingValue,
          engravingError: isEngravingError,
          length: measureLength,
          lengthError: lengthErr,
          lengthInputRef: lengthInput,
          setEngraving: (engrvng) => setEngravingValue(engrvng),
          setEngravingError: (error) => setIsEngravingError(error),
          setLengthError: (error) => setLengthErr(error),
          setLengthInputRef: (ref) => setLengthInput(ref),
          setShowEngravingError: (showError) => setIsShowEnravingError(showError),
          setWidthError: (error) => setWidthErr(error),
          setWidthInputRef: (ref) => setWidthInput(ref),
          showEngravingError: isShowEngravingError,
          width: measureWidth,
          widthError: widthErr,
          widthInputRef: widthInput,
        },
        pageData: {
          isBot,
        },
        productData: {
          currentProductId: variationId || productId || fallbackId,
          error: product.error ? { description: product.error.message, statusCode: 404 } : null,
          isLoading: product.loading,
          printProductId,
          product: variantRef.current,
          setIsFallback: (flag) => setIsFallback(flag),
        },
        ratingData: {
          error: rating?.error ? { description: rating?.error?.message, statusCode: 404 } : null,
          isLoading: useDebounce(akl ? rating?.loading : true, 300),
          rating: ratingRef?.current,
        },
        seoData: {
          error: seo?.error ? { description: seo?.error.message, statusCode: 404 } : null,
          isLoading: akl ? seo?.loading : true,
          seoData: seo?.data,
        },
      }}
      // props need to be passed in this way
      // eslint-disable-next-line
      {...props}
    >
      <script
        type="text/javascript"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: `window.__productID="${serverProductId}"` }}
      />
      {children}
    </ProductContext.Provider>
  );
};

export default DataProvider;
